import React, { useState, useEffect, useMemo } from 'react';
import { Trash2, Plus, Info, AlertTriangle, Eye, EyeOff, BarChart, MessageSquare, Cpu } from 'lucide-react';
import DemocraticImpactVisualizer from './DemocraticImpactVisualizer';
import RhetoricalTacticsEditor from './RhetoricalTacticsEditor';
import StepByStepArticleAnalyzer from './StepByStepArticleAnalyzer';

const ArticleClaimExtractor = ({ articleId, articleContent, onClaimsChange, existingClaims = [], workflowButtons = null, isWorkflow = false }) => {
  const [claims, setClaims] = useState([]);
  const [newClaim, setNewClaim] = useState({
    claim_text: '',
    rebuttal_text: '',
    evidence_links: [],
    importance_level: 5, // Default to highest importance
    democratic_impact: '',
    rhetorical_tactics: []
  });
  const [newEvidenceLink, setNewEvidenceLink] = useState('');
  const [selectedText, setSelectedText] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showHighlights, setShowHighlights] = useState(true);

  // Initialize claims from existing claims if available
  useEffect(() => {
    if (existingClaims && existingClaims.length > 0) {
      setClaims(existingClaims.map(claim => ({
        ...claim,
        evidence_links: Array.isArray(claim.evidence_links) 
          ? claim.evidence_links 
          : (typeof claim.evidence_links === 'string' 
            ? JSON.parse(claim.evidence_links) 
            : [])
      })));
    }
  }, [existingClaims]);

  // Notify parent component when claims change
  useEffect(() => {
    if (onClaimsChange) {
      onClaimsChange(claims);
    }
  }, [claims, onClaimsChange]);

  // Handle selection of text from article content
  const handleTextSelection = () => {
    const selection = window.getSelection();
    if (selection && selection.toString().trim().length > 0) {
      setSelectedText(selection.toString().trim());
    }
  };
  
  // Create highlighted article content
  const highlightedContent = useMemo(() => {
    if (!articleContent || !showHighlights || claims.length === 0) {
      return articleContent;
    }

    let content = articleContent;
    let offset = 0;

    // Sort claims by their position in the article
    const sortedClaims = [...claims].sort((a, b) => {
      const posA = content.indexOf(a.claim_text);
      const posB = content.indexOf(b.claim_text);
      return posA - posB;
    });

    // Add highlight markup to each claim
    sortedClaims.forEach((claim, index) => {
      const claimText = claim.claim_text;
      const position = content.indexOf(claimText, offset);
      
      if (position === -1) return; // Skip if not found

      const importanceClass = `claim-importance-${claim.importance_level || 3}`;
      const highlightColors = [
        'bg-gray-200', // Level 1
        'bg-yellow-100', // Level 2
        'bg-yellow-200', // Level 3
        'bg-orange-100', // Level 4
        'bg-orange-200', // Level 5
      ];
      const highlightColor = highlightColors[claim.importance_level - 1] || highlightColors[2];
      
      const before = content.substring(0, position);
      const after = content.substring(position + claimText.length);
      
      // Create a highlighted span with data attributes for claim information
      content = `${before}<span class="claim-highlight ${importanceClass} ${highlightColor} rounded px-1 py-0.5" data-claim-id="${claim.id || index}" data-importance="${claim.importance_level}">${claimText}</span>${after}`;
      
      // Update offset to avoid re-highlighting parts of already highlighted text
      offset = position + claimText.length + `<span class="claim-highlight ${importanceClass} ${highlightColor} rounded px-1 py-0.5" data-claim-id="${claim.id || index}" data-importance="${claim.importance_level}">`.length;
    });

    return content;
  }, [articleContent, claims, showHighlights]);

  // Use selected text as claim text
  const useSelectedTextAsClaim = () => {
    if (selectedText) {
      setNewClaim(prev => ({ ...prev, claim_text: selectedText }));
      setSelectedText('');
    }
  };

  // Add a new evidence link to the current new claim
  const addEvidenceLink = () => {
    if (newEvidenceLink && newEvidenceLink.trim()) {
      try {
        // Validate URL format with built-in URL constructor
        new URL(newEvidenceLink);
        
        setNewClaim(prev => ({
          ...prev,
          evidence_links: [...prev.evidence_links, newEvidenceLink.trim()]
        }));
        setNewEvidenceLink('');
        setError(null);
      } catch (e) {
        setError('Please enter a valid URL');
      }
    }
  };

  // Remove an evidence link from the current new claim
  const removeEvidenceLink = (index) => {
    setNewClaim(prev => ({
      ...prev,
      evidence_links: prev.evidence_links.filter((_, i) => i !== index)
    }));
  };

  // Remove an evidence link from an existing claim
  const removeEvidenceLinkFromClaim = (claimIndex, linkIndex) => {
    setClaims(prev => {
      const updatedClaims = [...prev];
      const claim = { ...updatedClaims[claimIndex] };
      claim.evidence_links = claim.evidence_links.filter((_, i) => i !== linkIndex);
      updatedClaims[claimIndex] = claim;
      return updatedClaims;
    });
  };
  
  // Add evidence link to an existing claim
  const [claimEvidenceLinks, setClaimEvidenceLinks] = useState({});
  
  const handleClaimEvidenceLinkChange = (claimIndex, value) => {
    setClaimEvidenceLinks({
      ...claimEvidenceLinks,
      [claimIndex]: value
    });
  };
  
  const addEvidenceLinkToClaim = (claimIndex) => {
    const linkValue = claimEvidenceLinks[claimIndex];
    if (!linkValue || !linkValue.trim()) return;
    
    try {
      // Validate URL format
      new URL(linkValue);
      
      setClaims(prev => {
        const updatedClaims = [...prev];
        const claim = { ...updatedClaims[claimIndex] };
        claim.evidence_links = [...(claim.evidence_links || []), linkValue.trim()];
        updatedClaims[claimIndex] = claim;
        return updatedClaims;
      });
      
      // Clear the input
      setClaimEvidenceLinks({
        ...claimEvidenceLinks,
        [claimIndex]: ''
      });
      
      setError(null);
    } catch (e) {
      setError('Please enter a valid URL for the evidence link');
    }
  };

  // Add a new claim to the list
  const addClaim = () => {
    if (!newClaim.claim_text.trim()) {
      setError('Claim text is required');
      return;
    }

    // Generate a temporary ID for new claims
    const tempId = `temp_${Date.now()}_${Math.floor(Math.random() * 1000)}`;

    setClaims(prev => [
      ...prev,
      {
        ...newClaim,
        id: tempId,
        article_id: articleId
      }
    ]);

    // Reset the form
    setNewClaim({
      claim_text: '',
      rebuttal_text: '',
      evidence_links: [],
      importance_level: 5,
      democratic_impact: '',
      rhetorical_tactics: []
    });
    setError(null);
  };

  // Remove a claim from the list
  const removeClaim = (index) => {
    setClaims(prev => prev.filter((_, i) => i !== index));
  };

  // Update existing claim properties
  const updateClaim = (index, field, value) => {
    setClaims(prev => {
      const updatedClaims = [...prev];
      updatedClaims[index] = {
        ...updatedClaims[index],
        [field]: value
      };
      return updatedClaims;
    });
  };

  // Save all claims to the backend
  const saveAllClaims = async () => {
    if (claims.length === 0) {
      setError('Please add at least one claim');
      return;
    }

    setLoading(true);
    setError(null);

    console.log('Preparing to save claims for article:', articleId);
    console.log('Claims data:', JSON.stringify(claims, null, 2));

    try {
      console.log(`Sending POST request to /api/articles/${articleId}/claims`);
      const payload = { claims: claims };
      console.log('Request payload:', JSON.stringify(payload, null, 2));
      
      const response = await fetch(`/api/articles/${articleId}/claims`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      console.log('Response status:', response.status, response.statusText);
      console.log('Response headers:', JSON.stringify(Object.fromEntries([...response.headers]), null, 2));

      if (!response.ok) {
        const responseText = await response.text();
        console.error('Error response body (raw):', responseText);
        
        try {
          // Try to parse as JSON if possible
          const errorData = JSON.parse(responseText);
          console.error('Error response data (parsed):', errorData);
          throw new Error(errorData.error || errorData.details || 'Failed to save claims');
        } catch (jsonError) {
          // Handle case where the response couldn't be parsed as JSON
          console.error('Could not parse error response as JSON:', jsonError);
          throw new Error(`Failed to save claims: Server returned ${response.status} ${response.statusText}`);
        }
      }

      // Update claims with server-assigned IDs
      const resultText = await response.text();
      console.log('Success response body (raw):', resultText);
      
      try {
        const result = JSON.parse(resultText);
        console.log('Success response data (parsed):', result);
        
        if (result.claims) {
          console.log('Setting claims with server IDs');
          setClaims(result.claims);
          
          // Only show the confirmation dialog if we're NOT in the integrated workflow
          // We can detect this by checking if the parent component passed an onClaimsChange callback
          const isInWorkflow = !!onClaimsChange;
          
          if (!isInWorkflow) {
            // Show success message with option to complete analysis (only in classic flow)
            const shouldAnalyze = window.confirm(
              "Claims saved successfully! Would you like to run a full analysis with Claude AI now to add ratings and democratic impact scores?"
            );
            
            if (shouldAnalyze && result.claims.length > 0) {
              // Trigger Claude analysis if user confirms
              document.getElementById('claude-analyze-button')?.click();
            }
          }
        }
      } catch (jsonError) {
        console.error('Could not parse success response as JSON:', jsonError);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error saving claims:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  // Handle Claude analysis completion
  const handleClaudeAnalysisComplete = (analysisResult) => {
    if (analysisResult && analysisResult.claims && analysisResult.claims.length > 0) {
      // Handle new claims from Claude
      setClaims(prevClaims => {
        // Create a map of existing claims by text to avoid duplicates
        const existingClaimMap = new Map(
          prevClaims.map(claim => [claim.claim_text, claim])
        );
        
        // Filter out claims that already exist (by text match)
        const newClaims = analysisResult.claims.filter(
          claim => !existingClaimMap.has(claim.claim_text)
        );
        
        // Combine existing claims with new unique claims
        return [...prevClaims, ...newClaims];
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Workflow buttons at the top if provided */}
      {workflowButtons && (
        <div className="mb-4">
          {workflowButtons}
        </div>
      )}
      
      {/* Claude AI Analysis Component - only show when not in workflow mode */}
      {!isWorkflow && (
        <StepByStepArticleAnalyzer 
          articleId={articleId}
          articleContent={articleContent}
          onAnalysisComplete={handleClaudeAnalysisComplete}
          existingClaims={claims}
        />
      )}

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Extract and Rebut Misleading Claims
          </h3>
          
          <span className="text-sm text-gray-500 italic">
            {claims.length > 0 ? `${claims.length} claims identified` : 'No claims identified yet'}
          </span>
        </div>

        {error && (
          <div className="p-4 mb-4 bg-red-50 border-l-4 border-red-500 text-red-700">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 mr-2" />
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Text selection functionality */}
        {articleContent && (
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-medium text-gray-700">Select Text from Article</h4>
              {claims.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowHighlights(!showHighlights)}
                  className="inline-flex items-center px-2 py-1 text-xs border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50"
                >
                  {showHighlights ? (
                    <>
                      <EyeOff className="h-3 w-3 mr-1" />
                      Hide Highlights
                    </>
                  ) : (
                    <>
                      <Eye className="h-3 w-3 mr-1" />
                      Show Highlights
                    </>
                  )}
                </button>
              )}
            </div>
            <div
              className="bg-gray-50 p-4 rounded-md border border-gray-200 max-h-60 overflow-y-auto"
              onMouseUp={handleTextSelection}
            >
              {showHighlights && claims.length > 0 ? (
                <div 
                  className="whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: highlightedContent }}
                />
              ) : (
                <div className="whitespace-pre-wrap">{articleContent}</div>
              )}
            </div>
            
            {selectedText && (
              <div className="mt-2 flex items-center">
                <div className="flex-1 bg-yellow-50 p-2 border border-yellow-200 rounded text-sm">
                  {selectedText}
                </div>
                <button
                  type="button"
                  onClick={useSelectedTextAsClaim}
                  className="ml-2 inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
                >
                  Use as Claim
                </button>
              </div>
            )}
            
            {showHighlights && claims.length > 0 && (
              <div className="mt-3 text-xs text-gray-500 flex space-x-4">
                <div className="font-medium">Importance Level:</div>
                {[5, 4, 3, 2, 1].map((level) => (
                  <div key={level} className="flex items-center">
                    <span className={`inline-block w-3 h-3 rounded mr-1 ${
                      level === 5 ? 'bg-orange-200' :
                      level === 4 ? 'bg-orange-100' :
                      level === 3 ? 'bg-yellow-200' :
                      level === 2 ? 'bg-yellow-100' :
                      'bg-gray-200'
                    }`} />
                    <span>Level {level}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* New claim form */}
        <div className="rounded-md bg-gray-50 p-4 mb-6">
          <h4 className="font-medium text-gray-700 mb-3">Add New Claim</h4>
          
          <div className="space-y-4">
            <div>
              <label htmlFor="claim_text" className="block text-sm font-medium text-gray-700 mb-1">
                Claim Text <span className="text-red-500">*</span>
              </label>
              <textarea
                id="claim_text"
                rows={3}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                value={newClaim.claim_text}
                onChange={(e) => setNewClaim(prev => ({ ...prev, claim_text: e.target.value }))}
                placeholder="Enter the misleading claim from the article"
              />
            </div>

            <div>
              <label htmlFor="rebuttal_text" className="block text-sm font-medium text-gray-700 mb-1">
                Rebuttal Text <span className="text-red-500">*</span>
              </label>
              <textarea
                id="rebuttal_text"
                rows={3}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                value={newClaim.rebuttal_text}
                onChange={(e) => setNewClaim(prev => ({ ...prev, rebuttal_text: e.target.value }))}
                placeholder="Provide factual information that rebuts this claim"
              />
            </div>

            <div>
              <label htmlFor="democratic_impact" className="block text-sm font-medium text-gray-700 mb-1">
                Democratic Impact
              </label>
              <textarea
                id="democratic_impact"
                rows={2}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                value={newClaim.democratic_impact}
                onChange={(e) => setNewClaim(prev => ({ ...prev, democratic_impact: e.target.value }))}
                placeholder="Explain how this claim impacts democratic institutions or norms"
              />
            </div>

            <div>
              <label htmlFor="importance_level" className="block text-sm font-medium text-gray-700 mb-1">
                Importance Level
              </label>
              <select
                id="importance_level"
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                value={newClaim.importance_level}
                onChange={(e) => setNewClaim(prev => ({ ...prev, importance_level: parseInt(e.target.value) }))}
              >
                <option value="5">Very High - Central to article (5)</option>
                <option value="4">High - Major claim (4)</option>
                <option value="3">Medium - Significant claim (3)</option>
                <option value="2">Low - Minor claim (2)</option>
                <option value="1">Very Low - Tangential claim (1)</option>
              </select>
            </div>
            
            <div className="border-t border-gray-200 pt-4 mt-4">
              <RhetoricalTacticsEditor 
                claim={newClaim.claim_text}
                onTacticsChange={(tactics) => setNewClaim(prev => ({ ...prev, rhetorical_tactics: tactics }))}
                existingTactics={newClaim.rhetorical_tactics}
              />
            </div>

            <div>
              <label htmlFor="evidence_link" className="block text-sm font-medium text-gray-700 mb-1">
                Evidence Links
              </label>
              <div className="flex space-x-2">
                <input
                  type="url"
                  id="evidence_link"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  value={newEvidenceLink}
                  onChange={(e) => setNewEvidenceLink(e.target.value)}
                  placeholder="https://example.com/evidence"
                  onKeyPress={(e) => e.key === 'Enter' && addEvidenceLink()}
                />
                <button
                  type="button"
                  onClick={addEvidenceLink}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
              
              {/* Evidence links list */}
              {newClaim.evidence_links.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {newClaim.evidence_links.map((link, index) => (
                    <li key={index} className="flex items-center justify-between text-sm bg-gray-100 p-2 rounded">
                      <a href={link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate flex-1">
                        {link}
                      </a>
                      <button
                        type="button"
                        onClick={() => removeEvidenceLink(index)}
                        className="text-red-500 hover:text-red-700 ml-2"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={addClaim}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none"
              >
                Add Claim
              </button>
            </div>
          </div>
        </div>

        {/* Claims list */}
        {claims.length > 0 && (
          <div>
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-medium text-gray-700">Claims ({claims.length})</h4>
              <button
                type="button"
                className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center"
                onClick={() => {
                  const claimsWithImpact = claims.filter(c => !c.democratic_impact);
                  if (claimsWithImpact.length > 0) {
                    alert('Please add democratic impact assessments to all claims to improve the visualization.');
                  }
                }}
              >
                <BarChart className="h-4 w-4 mr-1" />
                View Democratic Impact
              </button>
            </div>
            
            {/* Democratic Impact Visualization */}
            <DemocraticImpactVisualizer claims={claims} />
            
            <div className="space-y-4 mt-4">
              {claims.map((claim, index) => (
                <div key={claim.id || index} className="border border-gray-200 rounded-md p-4 bg-white">
                  <div className="flex justify-between items-start mb-2">
                    <h5 className="font-medium text-gray-900">Claim #{index + 1}</h5>
                    <div className="flex items-center">
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full mr-2">
                        Importance: {claim.importance_level}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeClaim(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Claim Text
                    </label>
                    <div className="bg-red-50 border border-red-100 rounded p-2 text-sm">
                      {claim.claim_text}
                    </div>
                  </div>
                  
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Rebuttal
                    </label>
                    <textarea
                      rows={2}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
                      value={claim.rebuttal_text}
                      onChange={(e) => updateClaim(index, 'rebuttal_text', e.target.value)}
                    />
                  </div>
                  
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Democratic Impact
                    </label>
                    <textarea
                      rows={2}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-blue-500 focus:border-blue-500"
                      value={claim.democratic_impact}
                      onChange={(e) => updateClaim(index, 'democratic_impact', e.target.value)}
                      placeholder="Describe how this claim impacts democratic institutions or norms (e.g. free elections, rule of law, free speech)"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Evidence Links ({claim.evidence_links.length})
                    </label>
                    
                    {/* Evidence link input */}
                    <div className="flex space-x-1 mb-2">
                      <input
                        type="url"
                        className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        value={claimEvidenceLinks[index] || ''}
                        onChange={(e) => handleClaimEvidenceLinkChange(index, e.target.value)}
                        placeholder="https://example.com/evidence"
                        onKeyPress={(e) => e.key === 'Enter' && addEvidenceLinkToClaim(index)}
                      />
                      <button
                        type="button"
                        onClick={() => addEvidenceLinkToClaim(index)}
                        className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                    
                    {claim.evidence_links.length > 0 ? (
                      <ul className="space-y-1">
                        {claim.evidence_links.map((link, linkIndex) => (
                          <li key={linkIndex} className="flex items-center justify-between text-xs bg-gray-100 p-1.5 rounded">
                            <a href={link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate flex-1">
                              {link}
                            </a>
                            <button
                              type="button"
                              onClick={() => removeEvidenceLinkFromClaim(index, linkIndex)}
                              className="text-red-500 hover:text-red-700 ml-2"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-gray-500 italic">No evidence links added</p>
                    )}
                  </div>
                  
                  <div className="mt-3 border-t border-gray-200 pt-3">
                    <label className="block text-xs font-medium text-gray-500 mb-1 flex items-center">
                      <MessageSquare className="h-3 w-3 mr-1" />
                      Rhetorical Tactics
                    </label>
                    <RhetoricalTacticsEditor 
                      claim={claim.claim_text}
                      onTacticsChange={(tactics) => updateClaim(index, 'rhetorical_tactics', tactics)}
                      existingTactics={claim.rhetorical_tactics || []}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Save button - only show if there are claims to save and not in workflow mode */}
        {claims.length > 0 && !workflowButtons && (
          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={saveAllClaims}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save All Claims'}
            </button>
          </div>
        )}
      </div>

      {/* Help section */}
      <div className="bg-blue-50 rounded-lg p-4 shadow-sm">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <h4 className="font-medium mb-1">How to Extract Claims</h4>
            <ol className="list-decimal list-inside space-y-1 ml-1">
              <li>Select text in the article to quickly capture a claim</li>
              <li>Add a factual rebuttal with supporting evidence</li>
              <li>Rate the importance of each claim (higher = more important)</li>
              <li>Identify the rhetorical tactics used in the claim</li>
              <li>Add evidence links to back up your rebuttals</li>
              <li>Assess the claim's impact on democratic institutions</li>
              <li>Click "Save All Claims" when you're finished</li>
            </ol>
            <p className="mt-2 text-xs border-t border-blue-200 pt-2">
              <strong>New:</strong> Identifying rhetorical tactics helps readers understand the persuasive techniques 
              being used and encourages critical thinking about information sources.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArticleClaimExtractor;